import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { publicAPI } from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import ErrorMessage from '../../components/ErrorMessage'
import { useTheme } from '../../contexts/ThemeContext'
import { localizeApiDetail } from '../../i18n/apiError'
import { useLatest } from '../Bruktbil/useLatest'
import { fuelColor, int, pct, visibleTops, withEkskluder } from './drivstoff'
import type { AkseldriftQuery, DrivstoffAlder as Alder, FuelModelRow, Periods } from './types'

const pctAxis = (v: number) => `${v.toLocaleString('nb-NO', { maximumFractionDigits: 1 })} %`

/** Teknisk › Drivstoff › Alder: owner age groups × fuel category, optionally for one brand or model (AC12). */
export default function DrivstoffAlder({ queryKey, ekskluderElbil, onPeriods }: {
  queryKey: string
  ekskluderElbil: boolean
  /** Reports the periods of the latest answer, for the explanation box */
  onPeriods: (periods: Periods) => void
}) {
  const { t } = useTranslation('teknisk')
  const { isDark } = useTheme()
  const [models, setModels] = useState<FuelModelRow[]>([])
  const [merke, setMerke] = useState('')
  const [modelId, setModelId] = useState('')
  const [alder, setAlder] = useState<Alder | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // The brand and model choices: models sold in the selection (cached on the server)
  useLatest(alive => {
    publicAPI.getDrivstoffModeller(JSON.parse(queryKey))
      .then(res => { if (alive()) setModels(res.data.data) })
      .catch(() => { if (alive()) setModels([]) })
  }, [queryKey])

  useLatest(alive => {
    const params: AkseldriftQuery & { merke?: string; master_model_id?: number } =
      withEkskluder(JSON.parse(queryKey), ekskluderElbil)
    if (modelId) params.master_model_id = Number(modelId)
    else if (merke) params.merke = merke
    setLoading(true)
    setError(null)
    publicAPI.getDrivstoffAlder(params)
      .then(res => {
        if (!alive()) return
        setAlder(res.data)
        onPeriods(res.data.periods)
      })
      .catch(err => {
        if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('drivstoff.loadError'))
      })
      .finally(() => { if (alive()) setLoading(false) })
  }, [queryKey, ekskluderElbil, merke, modelId])

  const brands = useMemo(() => [...new Set(models.map(m => m.brand))].sort((a, b) => a.localeCompare(b, 'nb')), [models])
  const brandModels = useMemo(
    () => models.filter(m => m.brand === merke).sort((a, b) => a.model.localeCompare(b.model, 'nb')),
    [models, merke],
  )

  const tops = visibleTops(ekskluderElbil)
  const label = (k: (typeof tops)[number]) => t(`drivstoff.category.${k}` as const)
  const axisColor = isDark ? '#94a3b8' : '#64748b'
  const gridColor = isDark ? '#334155' : '#e2e8f0'
  const surface = isDark ? '#1e293b' : '#ffffff'

  return (
    <div className="space-y-6" data-testid="drivstoff-alder">
      <div className="card p-4 flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="drivstoff-alder-merke" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('drivstoff.alder.brand')}
          </label>
          <select
            id="drivstoff-alder-merke"
            value={merke}
            onChange={e => { setMerke(e.target.value); setModelId('') }}
            className="input-field"
          >
            <option value="">{t('drivstoff.alder.allBrands')}</option>
            {brands.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="drivstoff-alder-modell" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('drivstoff.alder.model')}
          </label>
          <select
            id="drivstoff-alder-modell"
            value={modelId}
            onChange={e => setModelId(e.target.value)}
            disabled={!merke}
            className="input-field disabled:opacity-50"
          >
            <option value="">{t('drivstoff.alder.allModels')}</option>
            {brandModels.map(m => <option key={m.master_model_id} value={m.master_model_id}>{m.model}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : alder && (
        <>
          <div className="card p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('drivstoff.alder.chartTitle')}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {ekskluderElbil ? t('drivstoff.alder.chartHintExcluded') : t('drivstoff.alder.chartHint')}
            </p>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={alder.groups.map(g => ({ age_group: g.age_group, ...g.shares }))} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                  <XAxis dataKey="age_group" stroke={axisColor} fontSize={12} />
                  <YAxis domain={[0, 100]} tickFormatter={(v: number) => `${v} %`} stroke={axisColor} fontSize={12} width={48} />
                  <Tooltip formatter={(v: number) => pctAxis(v)} />
                  <Legend />
                  {tops.map(k => (
                    <Bar key={k} dataKey={k} name={label(k)} stackId="share" fill={fuelColor(k, isDark)}
                      stroke={surface} strokeWidth={1} isAnimationActive={false} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card p-4">
            <div className="overflow-x-auto">
              <table className="data-table data-table-compact" data-testid="drivstoff-alder-table">
                <thead>
                  <tr>
                    <th>{t('drivstoff.alder.ageGroup')}</th>
                    <th className="text-right border-r-2 border-gray-300 dark:border-gray-600">{t('drivstoff.alder.count')}</th>
                    {tops.map(k => <th key={k} className="text-right">{label(k)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {alder.groups.map(g => (
                    <tr key={g.age_group}>
                      <td>{g.age_group}</td>
                      <td className="text-right border-r-2 border-gray-300 dark:border-gray-600">{int(g.total)}</td>
                      {tops.map(k => <td key={k} className="text-right">{pct(g.shares[k] ?? null)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-testid="drivstoff-alder-note">
              {t('drivstoff.alder.withoutAge', { count: int(alder.without_age) })}
            </p>
          </div>
        </>
      )}
    </div>
  )
}
